import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import AppointmentActionModal from './AppointmentActionModal';
import '../../styles/calendar.css';

const AppointmentCalendar = ({
  appointments,
  vets,
  selectedVet,
  selectedDate,
  onDateChange,
  onComplete,
  onCancel,
  onNoShow,
  onViewHistory,
  showToast,
  getStatusColor
}) => {
  const calendarRef = useRef(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 640);
  const [isTablet, setIsTablet] = useState(window.innerWidth >= 640 && window.innerWidth < 1024);

  // Action modal state
  const [actionModal, setActionModal] = useState({
    isOpen: false,
    appointment: null,
    isLoading: false
  });

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width < 640);
      setIsTablet(width >= 640 && width < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigate calendar when selectedDate changes
  useEffect(() => {
    if (calendarRef.current) {
      const calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(selectedDate);
    }
  }, [selectedDate]);

  // Transform appointments to FullCalendar events
  const events = useMemo(() => {
    return appointments.map(apt => {
      const startDate = new Date(apt.appointment_date);
      const endDate = new Date(startDate);
      endDate.setMinutes(endDate.getMinutes() + (apt.duration_minutes || 30));

      return {
        id: apt.id,
        title: apt.pets?.name || 'Ραντεβού',
        start: startDate,
        end: endDate,
        backgroundColor: getStatusColor(apt.status),
        borderColor: 'transparent',
        extendedProps: {
          ...apt,
          ownerName: `${apt.owners?.first_name || ''} ${apt.owners?.last_name || ''}`.trim(),
          vetName: `${apt.vets?.first_name || ''} ${apt.vets?.last_name || ''}`.trim(),
          petSpecies: apt.pets?.species || ''
        }
      };
    });
  }, [appointments, getStatusColor]);

  // Custom event content renderer
  const renderEventContent = useCallback((eventInfo) => {
    const { extendedProps } = eventInfo.event;

    return (
      <div className="fc-event-content">
        <span className="event-pet-name">{eventInfo.event.title}</span>
        {extendedProps.ownerName && (
          <span className="event-owner-name">{extendedProps.ownerName}</span>
        )}
        {extendedProps.client_phone && (
          <span className="event-phone">{extendedProps.client_phone}</span>
        )}
        {selectedVet === 'all' && extendedProps.vetName && (
          <span className="event-vet-name">{extendedProps.vetName}</span>
        )}
      </div>
    );
  }, [selectedVet]);

  // Handle event click - open action modal
  const handleEventClick = useCallback((clickInfo) => {
    const appointment = {
      id: clickInfo.event.id,
      ...clickInfo.event.extendedProps
    };

    setActionModal({
      isOpen: true,
      appointment,
      isLoading: false
    });
  }, []);

  // Handle complete action
  const handleComplete = useCallback(async (appointmentId) => {
    setActionModal(prev => ({ ...prev, isLoading: true }));

    try {
      await onComplete(appointmentId);
      setActionModal({
        isOpen: false,
        appointment: null,
        isLoading: false
      });
    } catch (error) {
      setActionModal(prev => ({ ...prev, isLoading: false }));
    }
  }, [onComplete]);

  // Handle cancel action
  const handleCancel = useCallback(async (appointmentId) => {
    setActionModal(prev => ({ ...prev, isLoading: true }));

    try {
      await onCancel(appointmentId);
      setActionModal({
        isOpen: false,
        appointment: null,
        isLoading: false
      });
    } catch (error) {
      setActionModal(prev => ({ ...prev, isLoading: false }));
    }
  }, [onCancel]);

  // Handle no-show action
  const handleNoShow = useCallback(async (appointmentId) => {
    setActionModal(prev => ({ ...prev, isLoading: true }));

    try {
      await onNoShow(appointmentId);
      setActionModal({
        isOpen: false,
        appointment: null,
        isLoading: false
      });
    } catch (error) {
      setActionModal(prev => ({ ...prev, isLoading: false }));
    }
  }, [onNoShow]);

  // Handle view history
  const handleViewHistory = useCallback((petId) => {
    setActionModal({
      isOpen: false,
      appointment: null,
      isLoading: false
    });
    onViewHistory(petId);
  }, [onViewHistory]);

  // Close action modal
  const handleCloseModal = useCallback(() => {
    setActionModal({
      isOpen: false,
      appointment: null,
      isLoading: false
    });
  }, []);

  // Handle date click
  const handleDateClick = useCallback((dateInfo) => {
    onDateChange?.(dateInfo.date);
  }, [onDateChange]);

  // Handle dates set (view navigation)
  const handleDatesSet = useCallback((dateInfo) => {
    const newDate = dateInfo.start;
    if (newDate.getTime() !== selectedDate.getTime()) {
      onDateChange?.(newDate);
    }
  }, [onDateChange, selectedDate]);

  // Determine default view based on screen size
  const getDefaultView = () => {
    if (isMobile) return 'timeGridDay';
    if (isTablet) return 'timeGridDay';
    return 'timeGridWeek';
  };

  // Calendar plugins (free only)
  const plugins = useMemo(() => {
    return [dayGridPlugin, timeGridPlugin];
  }, []);

  return (
    <>
      <div className="rounded-xl overflow-hidden border" style={{ backgroundColor: '#111827', borderColor: '#374151', boxShadow: '0 10px 20px rgba(0,0,0,0.25)' }}>
        <FullCalendar
          ref={calendarRef}
          plugins={plugins}
          initialView={getDefaultView()}
          initialDate={selectedDate}
          events={events}

          // Time settings - show full 24h, scroll to 8am like Google Calendar
          slotMinTime="00:00:00"
          slotMaxTime="24:00:00"
          scrollTime="08:00:00"
          slotDuration="00:15:00"
          snapDuration="00:15:00"

          // Display settings
          allDaySlot={false}
          nowIndicator={true}
          navLinks={true}
          dayMaxEvents={3}
          weekends={true}

          // No drag-drop
          editable={false}
          selectable={false}

          // Locale settings (Greek)
          locale="el"
          firstDay={1}

          // Header toolbar
          headerToolbar={{
            left: 'prev,next today',
            center: 'title',
            right: 'timeGridDay,timeGridWeek'
          }}

          // Button text (Greek)
          buttonText={{
            today: 'Σήμερα',
            day: 'Ημέρα',
            week: 'Εβδομάδα'
          }}

          // Views configuration
          views={{
            timeGridDay: {
              titleFormat: { year: 'numeric', month: 'long', day: 'numeric', weekday: 'long' }
            },
            timeGridWeek: {
              titleFormat: { year: 'numeric', month: 'long', day: 'numeric' }
            }
          }}

          // Event handlers
          eventContent={renderEventContent}
          eventClick={handleEventClick}
          datesSet={handleDatesSet}

          // Height - fixed height enables scrolling through all 24 hours
          height={1400}

          // Empty state message
          noEventsContent="Δεν υπάρχουν ραντεβού"
        />
      </div>

      {/* Appointment Action Modal */}
      <AppointmentActionModal
        isOpen={actionModal.isOpen}
        onClose={handleCloseModal}
        onComplete={handleComplete}
        onCancel={handleCancel}
        onNoShow={handleNoShow}
        onViewHistory={handleViewHistory}
        appointment={actionModal.appointment}
        isLoading={actionModal.isLoading}
      />
    </>
  );
};

export default AppointmentCalendar;
